import { Link } from 'react-router-dom';
import type { InsightArticle } from '@/mocks/insights';

interface InsightCardProps {
  article: InsightArticle;
  variant?: 'default' | 'horizontal';
}

const categoryColorMap: Record<string, string> = {
  strategy: 'bg-amber-50 text-amber-700',
  design: 'bg-sky-50 text-sky-700',
  budget: 'bg-emerald-50 text-emerald-700',
  renovation: 'bg-rose-50 text-rose-700',
  brand: 'bg-violet-50 text-violet-700',
};

const categoryLabelMap: Record<string, string> = {
  strategy: '空間策略',
  design: '設計觀點',
  budget: '預算規劃',
  renovation: '翻新改造',
  brand: '品牌空間',
};

export default function InsightCard({ article, variant = 'default' }: InsightCardProps) {
  const tagColor = categoryColorMap[article.category] ?? 'bg-gray-50 text-gray-600';
  const tagLabel = categoryLabelMap[article.category] ?? article.category;

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/insights/${article.id}`}
        className="group flex gap-5 cursor-pointer"
      >
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden flex-shrink-0">
          <img
            src={article.coverImg}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col justify-between py-1 flex-1 min-w-0">
          <div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tagColor}`}>{tagLabel}</span>
            <h4 className="text-[#1a1a1a] font-semibold leading-snug mt-2 text-sm group-hover:text-[#b8956a] transition-colors line-clamp-2">
              {article.title}
            </h4>
          </div>
          <div className="flex items-center gap-3 text-[#9a9a9a] text-xs mt-2">
            <span>{article.publishDate.replace(/-/g, '.')}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/insights/${article.id}`}
      className="group flex flex-col rounded-xl overflow-hidden bg-white border border-[#ede9e4] hover:border-[#c8a87a]/50 transition-all duration-300 cursor-pointer"
    >
      {/* Cover */}
      <div className="h-[220px] overflow-hidden flex-shrink-0">
        <img
          src={article.coverImg}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600"
        />
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${tagColor}`}>{tagLabel}</span>
          <span className="text-[#c0b8b0] text-xs">{article.readTime}閱讀</span>
        </div>
        <h3
          className="font-semibold text-[#1a1a1a] leading-[1.45] mb-2 group-hover:text-[#b8956a] transition-colors"
          style={{ fontSize: '1.0625rem' }}
        >
          {article.title}
        </h3>
        <p className="text-[#6a6a6a] text-sm leading-[1.7] mb-4 line-clamp-2 flex-1">
          {article.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs text-[#9a8a7a] bg-[#f4f2ef] px-2 py-0.5 rounded-full">
              #{tag}
            </span>
          ))}
        </div>

        {/* Author + Date */}
        <div className="flex items-center justify-between pt-4 border-t border-[#f0ede8]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
              <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
            </div>
            <span className="text-[#5a5a5a] text-xs font-medium">{article.author}</span>
          </div>
          <span className="text-[#b0a898] text-xs">{article.publishDate.replace(/-/g, '.')}</span>
        </div>
      </div>
    </Link>
  );
}
